import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import axios from 'axios';
import './styles.css'; // Import your CSS file for chat styling
import ChatMessage, { ChatMessageProps } from './ChatMessage';
import CategorySelect from './CategorySelect';
import LoadingOverlay from '../../Components/LoadingOverlay';
import { useUser } from '../../hooks/GlobalUserHooks';

interface JobDescriptionFormProps {
  jobDescription: string;
  setJobDescription: React.Dispatch<React.SetStateAction<string>>;
  jobTitle: string;
  setJobTitle: React.Dispatch<React.SetStateAction<string>>;
  jobSummary: string;
  setJobSummary: React.Dispatch<React.SetStateAction<string>>;
  jobDetail: string;
  setJobDetail: React.Dispatch<React.SetStateAction<string>>;
  jobCategories: string[]; // Changed from jobCategory
  setJobCategories: React.Dispatch<React.SetStateAction<string[]>>; // Changed from setJobCategory
  complete: boolean;
  setComplete: React.Dispatch<React.SetStateAction<boolean>>;
  chatMessages: ChatMessageProps[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessageProps[]>>;
  seoUrl: string;
  setSeoUrl: React.Dispatch<React.SetStateAction<string>>;
}

export const JobDescriptionForm: React.FC<JobDescriptionFormProps> = ({
  jobDescription,
  setJobDescription,
  jobTitle,
  setJobTitle,
  jobSummary,
  setJobSummary,
  jobDetail,
  setJobDetail,
  jobCategories,
  setJobCategories,
  complete,
  setComplete,
  chatMessages,
  setChatMessages,
  seoUrl,
  setSeoUrl
}) => {
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string, content: string }>>([]);
  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state

  const { userName, currentUserId } = useUser();

  const handleMarkComplete = () => {
    setComplete(true);
    const newMessage: ChatMessageProps =
    {
      message: "The description has been marked complete by the user",
      role: 'BidBro',
      timestamp: new Date().toISOString(),
      displayName: userName
    };
    setChatMessages([...chatMessages, newMessage]);
    // Update the job status in the database
  };

  const handleCategoryChange = (categories: string[]) => {
    setJobCategories(categories);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true); // Start loading

    const userMessage: ChatMessageProps =
    {
      message: jobDescription,
      role: 'User' as 'User',
      timestamp: new Date().toISOString(),
      displayName: userName
    };
    setChatMessages((prevMessages) => [...prevMessages, userMessage]);
    // Update conversation history
    setConversationHistory(prev => [...prev, { role: 'user', content: jobDescription }]);

    setHasSubmittedOnce(true);

    const serverUrl = process.env.REACT_APP_SERVER;
    const apiUrl = `${serverUrl}/analyze`;

    try {
      const response: any = await axios.post(apiUrl, {
        content: jobDescription,
        conversationHistory: conversationHistory
      });

      console.log('Response received:', response);
      const analysis = JSON.parse(response.data?.analysis || response.analysis);
      console.log('analysis:', analysis);

      // Replace this section in your JobDescriptionForm.tsx handleSubmit function:

      var { complete, neededDetail, jobTitle, jobSummary, jobDetail, seoUrl } = analysis;

      // Format neededDetail as HTML unordered list
      if (Array.isArray(neededDetail)) {
        // Handle array format (existing logic)
        neededDetail = `
    <p>I need some more information to complete the job description:</p>
    <ul>
      ${neededDetail.map(detail => `<li>${detail}</li>`).join('')}
    </ul>
  `;
      } else if (typeof neededDetail === 'string' && neededDetail.trim() !== '') {
        // Handle string format with newline-separated items
        const lines = neededDetail
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => {
            // Remove leading dash and spaces if present
            return line.replace(/^-\s*/, '').trim();
          })
          .filter(line => line.length > 0);

        if (lines.length > 0) {
          neededDetail = `
      <p>I need some more information to complete the job description:</p>
      <ul>
        ${lines.map(detail => `<li>${detail}</li>`).join('')}
      </ul>
    `;
        } else {
          // Fallback for non-bulleted string content
          neededDetail = `<p>${neededDetail}</p>`;
        }
      } else if (neededDetail === "" || !neededDetail) {
        neededDetail = "<p>Thank you. This is enough information for now. Contractors may ask for more details before providing you a quote. You may proceed to the next step.</p>";
      } else {
        // Fallback for any other format
        neededDetail = `<p>${neededDetail}</p>`;
      }

      const completeMessage = "Your description has enough detail to submit. Keep in mind that contractors may ask for more detail about the job in an effort to price the job before a job site inspection. " + jobDetail;
      const incompleteMessage = "Your description does not have enough detail to submit.  " + neededDetail;

      const mainResponse = neededDetail; //analysis.complete ? completeMessage  : incompleteMessage;

      setConversationHistory(prev => [...prev, { role: 'assistant', content: mainResponse }]);

      const systemMessage: ChatMessageProps = {
        message: mainResponse,
        role: 'BidBro' as 'BidBro',
        timestamp: new Date().toISOString(),
        displayName: userName
      };

      setChatMessages((prevMessages) => [...prevMessages, systemMessage]);
      console.log(chatMessages);
      setJobDescription(jobDescription);

      // Update parent state with the response
      setComplete(complete);
      setJobCategories(jobCategories);
      setJobTitle(jobTitle);
      setJobSummary(jobSummary);
      setJobDetail(jobDetail);
      setSeoUrl(seoUrl);
      setJobDescription(''); //this is the text area that the customer is entering the data into

    } catch (error) {
      const errorMessage: ChatMessageProps =
      {
        message: 'Error analyzing job description',
        role: 'BidBro' as 'BidBro',
        timestamp: new Date().toISOString(),
        displayName: userName
      };
      setChatMessages((prevMessages) => [...prevMessages, errorMessage]);
      console.error('Error analyzing job description:', error);
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  //console.log('chatMessages:', chatMessages);
  //console.log('displayName:', userName);

  return (
    <div className="job-description-form">
      {/* Loading overlay for API call - REMOVED absolute={true} */}
      <LoadingOverlay
        isVisible={isSubmitting}
        size="medium"
        text="Analyzing Job Description"
        subtext="BidBro AI is reviewing your details..."
        debug={false}
      />

       <div className="chat-container">
        {(chatMessages || []).map((message, index) => (
          <div key={index} className={`message-wrapper ${message.displayName === userName && message.role !== 'BidBro' ? 'right' : 'left'}`}>
            <ChatMessage
              message={message.message}
              role={message.role}
              timestamp={message.timestamp}
              displayName={message.displayName}
            />
          </div>
        ))}
      </div>

      <div className="mb-4">
        <CategorySelect
          selectedCategories={jobCategories}
          onChange={(categories: string[]) => {
            setJobCategories(categories);
          }}
          maxSelections={5}
        />
      </div>

      {/* Only show the form elements if complete is false */}
      {!complete && (
        <>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enter a description for the job
          </label>

          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Job Description"
            className="textarea"
            rows={8}
            disabled={isSubmitting} // Disable input while submitting
          />
          <div className="right-div">
            <button
              id="markComplete"
              type="button"
              className="btn btn-success"
              style={{ marginRight: '5px' }}
              disabled={!hasSubmittedOnce || isSubmitting}
              onClick={handleMarkComplete}>Mark Complete
            </button>
            <UncontrolledTooltip
              target="markComplete"
              transition={{ timeout: 150 }} >
              Select this option if the requested information is unavailable and you wish to proceed to the next step.
            </UncontrolledTooltip>
            <button
              id="submitDescription"
              type="button"
              onClick={handleSubmit}
              className="submit-button"
              disabled={isSubmitting} // Disable button while submitting
            >
              {isSubmitting ? 'Analyzing...' : 'Submit Details'}
            </button>
            <UncontrolledTooltip
              target="submitDescription"
              transition={{ timeout: 150 }} >
              Share your job description or provide the additional details BidBro is asking for.
            </UncontrolledTooltip>
          </div>
        </>
      )}

      {/* Show completion message when complete is true */}
      {complete && (
        <div className="alert alert-success">
          <i className="ri-check-double-line me-2"></i>
          <strong>Job description complete!</strong> You can proceed to the next step.
        </div>
      )}
    </div>
  );
};